import type { ModelProps, ModelRegistry } from './types';

export function TreeModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group position={[0, 0, 0]}>
      {/* Trunk */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.18, 0.8, 6]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8b5a2b' : '#4a3a2a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Foliage layers */}
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[0, 1 + i * 0.4, 0]} castShadow>
          <coneGeometry args={[0.6 - i * 0.15, 0.6, 6]} />
          <meshStandardMaterial
            color={world === 'real' ? '#4a7c59' : '#2d4a3a'}
            roughness={0.7}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
    </group>
  );
}

export function AncientTreeModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const trunkColor = world === 'real' ? '#5d4037' : '#3a2a2a';
  const barkColor = world === 'real' ? '#4e342e' : '#2d1f1f';
  const foliageColor = world === 'real' ? '#2e7d32' : '#1b4d1f';
  const foliageLight = world === 'real' ? '#43a047' : '#2e7d32';
  const mossColor = world === 'real' ? '#558b2f' : '#33691e';

  return (
    <group position={[0, 0, 0]}>
      {/* Exposed roots */}
      {[0, 1, 2, 3, 4].map((i) => {
        const angle = (i * Math.PI * 2) / 5 + 0.3;
        const rootLength = 0.5 + Math.sin(i * 2) * 0.2;
        return (
          <mesh
            key={`root-${i}`}
            position={[Math.cos(angle) * 0.25, 0.08, Math.sin(angle) * 0.25]}
            rotation={[Math.PI / 6, angle, Math.PI / 8]}
            castShadow
          >
            <cylinderGeometry args={[0.03, 0.06, rootLength, 5]} />
            <meshStandardMaterial color={trunkColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
          </mesh>
        );
      })}

      {/* Main twisted trunk */}
      <mesh position={[0, 0.5, 0]} rotation={[0.05, 0, 0.03]} castShadow>
        <cylinderGeometry args={[0.18, 0.35, 1, 8]} />
        <meshStandardMaterial color={trunkColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>

      {/* Trunk mid section */}
      <mesh position={[0.05, 1.1, 0.03]} rotation={[-0.08, 0.3, 0.05]} castShadow>
        <cylinderGeometry args={[0.14, 0.2, 0.8, 7]} />
        <meshStandardMaterial color={barkColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>

      {/* Upper trunk */}
      <mesh position={[-0.02, 1.7, 0.05]} rotation={[0.1, -0.2, -0.05]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 0.6, 6]} />
        <meshStandardMaterial color={trunkColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>

      {/* Main foliage cluster */}
      <mesh position={[0, 2.1, 0]} castShadow>
        <dodecahedronGeometry args={[0.7, 1]} />
        <meshStandardMaterial color={foliageColor} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>

      {/* Secondary foliage clusters */}
      {[
        { pos: [0.4, 1.9, 0.2], size: 0.4 },
        { pos: [-0.35, 2.0, 0.15], size: 0.35 },
        { pos: [0.15, 2.4, -0.3], size: 0.45 },
        { pos: [-0.2, 2.3, 0.3], size: 0.38 },
      ].map((cluster, i) => (
        <mesh key={`foliage-${i}`} position={cluster.pos as [number, number, number]} castShadow>
          <icosahedronGeometry args={[cluster.size, 1]} />
          <meshStandardMaterial
            color={i % 2 === 0 ? foliageColor : foliageLight}
            roughness={0.75}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}

      {/* Moss patches */}
      {[0, 1, 2].map((i) => (
        <mesh key={`moss-${i}`} position={[0.15 - i * 0.1, 0.6 + i * 0.3, 0.1 + i * 0.05]} castShadow>
          <sphereGeometry args={[0.08, 6, 6]} />
          <meshStandardMaterial color={mossColor} roughness={1} transparent={isOccluding} opacity={opacity * 0.9} />
        </mesh>
      ))}

      {/* Glowing particles in spectral world */}
      {world === 'spectral' && (
        <>
          {[0, 1, 2, 3, 4].map((i) => (
            <mesh
              key={`particle-${i}`}
              position={[Math.sin(i * 1.5) * 0.5, 1.5 + Math.cos(i * 0.8) * 0.5, Math.cos(i * 1.2) * 0.5]}
            >
              <sphereGeometry args={[0.03, 8, 8]} />
              <meshStandardMaterial color="#90EE90" emissive="#90EE90" emissiveIntensity={0.8} transparent opacity={0.7} />
            </mesh>
          ))}
          <pointLight position={[0, 1.5, 0]} color="#90EE90" intensity={2} distance={3} />
        </>
      )}
    </group>
  );
}

export function BushModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group position={[0, 0.2, 0]}>
      {[
        [0, 0, 0],
        [0.15, 0.05, 0.1],
        [-0.12, 0.02, 0.12],
        [0.08, 0.1, -0.1],
        [-0.1, 0.08, -0.08],
      ].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]} castShadow>
          <icosahedronGeometry args={[0.15 - i * 0.015, 0]} />
          <meshStandardMaterial
            color={world === 'real' ? '#6b8e23' : '#3d5a3d'}
            roughness={0.8}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
    </group>
  );
}

export function PlantModel({ world }: ModelProps) {
  return (
    <group position={[0, 0, 0]}>
      {/* Stem */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <cylinderGeometry args={[0.03, 0.05, 0.3, 6]} />
        <meshStandardMaterial color="#5a8a4a" roughness={0.8} />
      </mesh>
      {/* Leaves */}
      {[0, 1, 2, 3].map((i) => (
        <mesh
          key={i}
          position={[
            Math.cos((i * Math.PI) / 2) * 0.12,
            0.25 + i * 0.05,
            Math.sin((i * Math.PI) / 2) * 0.12,
          ]}
          rotation={[Math.PI / 4, (i * Math.PI) / 2, 0]}
          castShadow
        >
          <coneGeometry args={[0.08, 0.2, 4]} />
          <meshStandardMaterial
            color={world === 'real' ? '#7cb342' : '#4a7c59'}
            roughness={0.7}
          />
        </mesh>
      ))}
    </group>
  );
}

export function FlowerModel({ world }: ModelProps) {
  return (
    <group position={[0, 0, 0]}>
      {/* Stem */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.03, 0.4, 6]} />
        <meshStandardMaterial color="#5a8a4a" roughness={0.8} />
      </mesh>
      {/* Petals */}
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh
          key={i}
          position={[
            Math.cos((i * Math.PI * 2) / 5) * 0.08,
            0.42,
            Math.sin((i * Math.PI * 2) / 5) * 0.08,
          ]}
          rotation={[0.3, (i * Math.PI * 2) / 5, 0]}
          castShadow
        >
          <sphereGeometry args={[0.06, 6, 6]} />
          <meshStandardMaterial
            color={world === 'real' ? '#ff69b4' : '#9370db'}
            emissive={world === 'spectral' ? '#9370db' : undefined}
            emissiveIntensity={world === 'spectral' ? 0.3 : 0}
            roughness={0.5}
          />
        </mesh>
      ))}
      {/* Center */}
      <mesh position={[0, 0.42, 0]} castShadow>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial color="#ffd700" roughness={0.4} />
      </mesh>
    </group>
  );
}

export function RockModel({ world, objectId, isOccluding, opacity = 1 }: ModelProps) {
  const rockRotation = objectId ? (objectId.charCodeAt(0) + (objectId.charCodeAt(objectId.length - 1) || 0)) * 0.1 : 0;
  return (
    <mesh position={[0, 0.15, 0]} rotation={[0, rockRotation, 0.1]} castShadow receiveShadow>
      <dodecahedronGeometry args={[0.25, 0]} />
      <meshStandardMaterial
        color={world === 'real' ? '#a0937d' : '#5a5a6e'}
        roughness={0.9}
        transparent={isOccluding}
        opacity={opacity}
      />
    </mesh>
  );
}

export function RocksModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group position={[0, 0, 0]}>
      {[
        { pos: [0, 0.1, 0], scale: 0.2 },
        { pos: [0.25, 0.08, 0.15], scale: 0.15 },
        { pos: [-0.2, 0.06, 0.1], scale: 0.12 },
        { pos: [0.1, 0.05, -0.2], scale: 0.1 },
      ].map((rock, i) => (
        <mesh key={i} position={rock.pos as [number, number, number]} castShadow>
          <dodecahedronGeometry args={[rock.scale, 0]} />
          <meshStandardMaterial
            color={world === 'real' ? '#b0a090' : '#606070'}
            roughness={0.9}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
    </group>
  );
}

export function PineTreeModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group position={[0, 0, 0]}>
      {/* Trunk */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 1, 6]} />
        <meshStandardMaterial
          color={world === 'real' ? '#5d4037' : '#3a2a2a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Snow-covered foliage layers */}
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} position={[0, 1.2 + i * 0.5, 0]} castShadow>
          <coneGeometry args={[0.7 - i * 0.15, 0.7, 8]} />
          <meshStandardMaterial
            color={world === 'real' ? '#2d5a3a' : '#1a3a2a'}
            roughness={0.8}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
    </group>
  );
}

export function SnowyPineModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group position={[0, 0, 0]}>
      {/* Trunk */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 1, 6]} />
        <meshStandardMaterial
          color={world === 'real' ? '#5d4037' : '#3a2a2a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Foliage with snow */}
      {[0, 1, 2, 3].map((i) => (
        <group key={i}>
          <mesh position={[0, 1.2 + i * 0.5, 0]} castShadow>
            <coneGeometry args={[0.7 - i * 0.15, 0.7, 8]} />
            <meshStandardMaterial
              color={world === 'real' ? '#2d5a3a' : '#1a3a2a'}
              roughness={0.8}
              transparent={isOccluding}
              opacity={opacity}
            />
          </mesh>
          {/* Snow cap */}
          <mesh position={[0, 1.4 + i * 0.5, 0]} castShadow>
            <coneGeometry args={[0.5 - i * 0.12, 0.3, 8]} />
            <meshStandardMaterial
              color="#f8f8ff"
              roughness={0.6}
              transparent={isOccluding}
              opacity={opacity}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

export function PalmTreeModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group position={[0, 0, 0]}>
      {/* Curved trunk */}
      <mesh position={[0.1, 0.8, 0]} rotation={[0, 0, 0.15]} castShadow>
        <cylinderGeometry args={[0.08, 0.15, 1.6, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8d6e63' : '#5a4a3a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Palm fronds */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i / 6) * Math.PI * 2;
        return (
          <mesh
            key={i}
            position={[0.15 + Math.cos(angle) * 0.1, 1.7, Math.sin(angle) * 0.1]}
            rotation={[0.8, angle, 0]}
            castShadow
          >
            <coneGeometry args={[0.15, 0.8, 4]} />
            <meshStandardMaterial
              color={world === 'real' ? '#4caf50' : '#2d5a3a'}
              roughness={0.7}
              transparent={isOccluding}
              opacity={opacity}
            />
          </mesh>
        );
      })}
    </group>
  );
}

export const naturalModels: ModelRegistry = {
  tree: TreeModel,
  ancient_tree: AncientTreeModel,
  bush: BushModel,
  plant: PlantModel,
  flower: FlowerModel,
  rock: RockModel,
  rocks: RocksModel,
  pine_tree: PineTreeModel,
  snowy_pine: SnowyPineModel,
  palm_tree: PalmTreeModel,
};
